/**
 * IP Assignment Agreement
 * For Intellectual Property contributions
 * Requires both contributor and assignee signatures
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generateIPAssignmentHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page {
      size: Letter;
      margin: 0.75in;
      @bottom-center {
        content: "Page " counter(page) " of " counter(pages);
        font-family: Cambria, Georgia, 'Times New Roman', serif;
        font-size: 9pt;
        color: #666;
      }
    }
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      color: #1a1a1a;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0;
    }
    h1 {
      text-align: center;
      font-size: 18pt;
      margin: 0 0 20pt 0;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      border-bottom: 2px solid #c9a961;
      padding-bottom: 14pt;
    }
    h2 {
      font-size: 13pt;
      margin: 24pt 0 10pt 0;
      font-weight: bold;
      border-bottom: 1px solid #bfbfbf;
      padding-bottom: 6pt;
      color: #262626;
    }
    p {
      margin: 0 0 14pt 0;
      text-align: justify;
    }
    .recital {
      margin-bottom: 14pt;
      text-align: justify;
    }
    .signature-section {
      margin-top: 40pt;
      padding-top: 24pt;
      border-top: 1px solid #ccc;
      page-break-inside: avoid;
    }
    .signature-block {
      margin-top: 30pt;
      padding: 20pt;
      border: 1px solid #ddd;
      border-radius: 4pt;
      background-color: #fafafa;
    }
    .signature-line {
      border-bottom: 2px solid #333;
      width: 3.5in;
      min-height: 0.6in;
      margin-bottom: 8pt;
      background-color: white;
    }
    .signature-label {
      font-size: 10pt;
      margin-top: 6pt;
      font-weight: 600;
    }
    .date-line {
      border-bottom: 1px solid #666;
      width: 2in;
      display: inline-block;
      margin-left: 8pt;
    }
  </style>
</head>
<body>
  <h1>Intellectual Property Assignment Agreement</h1>
  
  <p>
    This Intellectual Property Assignment Agreement (this "Agreement") is entered into 
    as of {{contribution_date}} (the "Effective Date"), by and between:
  </p>
  
  <p style="margin-left: 28pt;">
    <strong>ASSIGNOR:</strong> {{contributor_name}}, a member of {{una_name}}<br>
    <strong>ASSIGNEE:</strong> {{receiving_entity}}
  </p>
  
  <h2>RECITALS</h2>
  
  <div class="recital">
    <strong>A.</strong> Assignor is the owner of certain intellectual property rights 
    described as: {{ip_description}}.
  </div>
  
  <div class="recital">
    <strong>B.</strong> Assignor desires to contribute these intellectual property rights 
    to Assignee through private member contribution within the ecclesiastical jurisdiction 
    of {{una_name}}.
  </div>
  
  <div class="recital">
    <strong>C.</strong> Assignee desires to accept stewardship of these intellectual property 
    rights for the benefit of the membership and advancement of the UNA's sacred purposes.
  </div>
  
  <h2>AGREEMENT</h2>
  
  <p>
    NOW, THEREFORE, in consideration of the mutual covenants and agreements contained herein, 
    and for other good and valuable consideration, the receipt and sufficiency of which are 
    hereby acknowledged, the parties agree as follows:
  </p>
  
  <h2>1. ASSIGNMENT OF RIGHTS</h2>
  
  <p>
    Assignor hereby assigns, transfers, and conveys to Assignee all right, title, and 
    interest in and to the following intellectual property:
  </p>
  
  <p style="margin-left: 28pt;">
    <strong>Type:</strong> {{ip_type}}<br>
    <strong>Description:</strong> {{ip_description}}
    ${workflowData.ip_registration_number ? `<br><strong>Registration Number:</strong> {{ip_registration_number}}` : ''}
    ${workflowData.ip_registration_date ? `<br><strong>Registration Date:</strong> {{ip_registration_date}}` : ''}
    ${workflowData.ip_jurisdiction ? `<br><strong>Jurisdiction:</strong> {{ip_jurisdiction}}` : ''}
  </p>
  
  <h2>2. SCOPE OF ASSIGNMENT</h2>
  
  <p>
    This assignment includes all rights of ownership, use, reproduction, distribution, 
    modification, and all other rights associated with the intellectual property, whether 
    now existing or hereafter arising.
  </p>
  
  <h2>3. ECCLESIASTICAL JURISDICTION</h2>
  
  <p>
    This contribution is made within the ecclesiastical jurisdiction of {{una_name}} and 
    is not subject to public law gift or donation rules. This is a private member contribution 
    governed by private contract law and the constitutional provisions of the UNA.
  </p>
  
  <h2>4. PURPOSE</h2>
  
  <p>
    Assignee shall steward the contributed intellectual property for the following purposes: 
    {{ip_purpose}}
  </p>
  
  <h2>5. EFFECTIVE DATE</h2>
  
  <p>
    This assignment shall be effective as of {{contribution_date}}, and all rights shall 
    vest in Assignee as of that date.
  </p>
  
  <h2>6. GOVERNING LAW</h2>
  
  <p>
    This Agreement shall be governed by the constitutional provisions and ecclesiastical 
    jurisdiction of {{una_name}}, and by private contract law.
  </p>
  
  <div class="signature-section">
    <p><strong>IN WITNESS WHEREOF</strong>, the parties have executed this Agreement as of 
    the Effective Date.</p>
    
    <div class="signature-block">
      <strong>ASSIGNOR:</strong>
      <div class="signature-line"></div>
      <div class="signature-label">{{contributor_name}}</div>
      <div style="margin-top: 16pt;">
        Date: <span class="date-line"></span>
      </div>
    </div>
    
    <div class="signature-block" style="margin-top: 24pt;">
      <strong>ASSIGNEE:</strong>
      <div class="signature-line" data-signer-id="{{treasury_secretary_id}}" data-signature-role="primary_officer"></div>
      <div class="signature-label">{{treasury_secretary_name}}, {{primary_officer_role}}</div>
      <div class="signature-label">On behalf of {{receiving_entity}}</div>
      <div style="margin-top: 16pt;">
        Date: <span class="date-line"></span>
      </div>
    </div>
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}
